import { useCallback, useState } from "react";

import { useStatsigContext } from "../../elements-statsig-provider/utils";
import {
  HOST_HUB_NUX_CONFIG_KEY,
  HOST_HUB_NUX_DYNAMIC_CONFIG_NAME,
  HOST_HUB_WELCOME_DIALOG_NUX_KEY,
} from "../constants/host-hub-nux.consts";
import {
  HostHubNuxConfigValue,
  isHostHubNuxEligible,
  markHostHubNuxViewed,
} from "../utils/host-hub-nux.utils";

type StatsigDynamicConfigs = Record<
  string,
  (() => Record<string, HostHubNuxConfigValue>) | undefined
>;

// One-time welcome dialog on /host-hub, gated by the `web_2_nux` config
// (`host_hub_coachmark` enabled/expires) and persisted as viewed in localStorage.
export const useHostHubWelcomeDialog = ({
  isHost,
  ssoId,
}: {
  isHost: boolean;
  ssoId?: string;
}) => {
  const { dynamicConfigs, initialized } = useStatsigContext();
  const [hasDismissed, setHasDismissed] = useState(false);

  const nuxConfig =
    (dynamicConfigs as StatsigDynamicConfigs)?.[
      HOST_HUB_NUX_DYNAMIC_CONFIG_NAME
    ]?.() ?? {};
  const introConfig = nuxConfig[HOST_HUB_NUX_CONFIG_KEY] ?? {};

  const isEligible =
    initialized &&
    isHostHubNuxEligible({
      isHost,
      nuxConfig: introConfig,
      nuxKey: HOST_HUB_WELCOME_DIALOG_NUX_KEY,
      ssoId,
    });

  const isOpen = isEligible && !hasDismissed;

  const dismiss = useCallback(() => {
    markHostHubNuxViewed(ssoId, HOST_HUB_WELCOME_DIALOG_NUX_KEY);
    setHasDismissed(true);
  }, [ssoId]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        dismiss();
      }
    },
    [dismiss],
  );

  return { dismiss, handleOpenChange, isOpen };
};
